// src/services/p2p/p2p-stack-manager.interface.ts

import type { HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices, GroupStatus, TurnConfig } from "@/types/poc/libp2p";
import type { MessageHandlerResult, MessageStats } from "@/services/abstract/poc/messaging.service.interface";

/**
 * Configuration for initializing a P2P stack for a specific six.
 */
export interface P2PStackOptions {
  sixId: string;
  autoConnectLibp2p?: boolean;
  autoInitializeHelia?: boolean;
  autoInitializeMessaging?: boolean;
  /** Bootstrap node multiaddrs for P2P connectivity */
  bootstrapNodes: string[];
  swarmKey: string;
  fileListTopic: string;
  fileTransferTopic: string;
  onMessage?: MessageCallback;
  onStatusUpdate?: StatusUpdateCallback;
  onConnectionUpdate?: ConnectionUpdateCallback;
  /** TURN server credentials for WebRTC ICE */
  turnConfig?: TurnConfig;
}

export type MessageCallback = (result: MessageHandlerResult, stats: MessageStats) => void;
export type StatusUpdateCallback = (sixId: string, subscriberCounts: Record<string, number>) => void;
export type ConnectionUpdateCallback = (sixId: string, connections: Array<{
  peerId: string;
  address: string;
  isDirect: boolean;
  isRelay: boolean;
  direction: 'inbound' | 'outbound';
}>) => void;

/**
 * Metadata for managing a LibP2P node's lifecycle and state.
 * This stores all mutable state related to a node's operation.
 */
export interface NodeMetadata {
  status: GroupStatus;
  config: {
    groupId: string;
    relayAddresses: string[];
  };
  timers: {
    healthCheckInterval: NodeJS.Timeout | null;
    updateInterval: NodeJS.Timeout | null;
    reconnectInterval: NodeJS.Timeout | null;
    topicListBroadcastInterval: NodeJS.Timeout | null;
    sixIdAnnounceTimer: NodeJS.Timeout | null;
  };
  reconnectState: {
    attempts: number;
    maxAttempts: number;
    relayPeerId: string | null;
  };
  peerTracking: {
    connectionTimers: Map<string, NodeJS.Timeout>;
    blockedPeers: Set<string>;
    reconnectionIntervals: Map<string, NodeJS.Timeout>;
    connectionAttempts: Map<string, number>;
    lastConnectionAttempt: Map<string, number>;
  };
}

/**
 * Represents a single P2P stack instance for one six.
 */
export interface P2PStackInstance {
  sixId: string;
  libp2pNode: LibP2pWithServices | null;
  heliaNode: HeliaNode | null;
  subscriptions: { cleanup: () => void } | null;
  isInitialized: boolean;
  fileListBroadcastInterval: NodeJS.Timeout | null;
  subscriberCheckTimer: NodeJS.Timeout | null;
  nodeMetadata: NodeMetadata | null;
  fileListTopic?: string;
  onStatusUpdate?: StatusUpdateCallback;
  onConnectionUpdate?: ConnectionUpdateCallback;
  /** Mesh heartbeat interval for diagnostics (only one six per browser) */
  meshHeartbeatInterval?: NodeJS.Timeout | null;
}

/**
 * Result returned when initializing a stack.
 */
export interface InitializeStackResult {
  sixId: string;
  peerId: string;
  isSuccessful: boolean;
}

/**
 * Manager for multiple P2P stacks, one per six.
 *
 * Responsibilities:
 * - Create and initialize P2P stacks for each six
 * - Route operations to the correct six's stack
 * - Manage lifecycle of all stacks
 */
export abstract class P2PStackManager {
  /**
   * Initialize a P2P stack for a specific six.
   */
  abstract initializeStack(options: P2PStackOptions): Promise<InitializeStackResult>;

  /**
   * Shutdown a specific six's P2P stack.
   */
  abstract shutdownStack(sixId: string): Promise<void>;

  /**
   * Shutdown all P2P stacks.
   */
  abstract shutdownAllStacks(): Promise<void>;

  /**
   * Publish a message using a specific six's stack.
   */
  abstract publishMessage(sixId: string, topic: string, data: Uint8Array | string): Promise<void>;

  /**
   * Get LibP2P node for a specific six.
   */
  abstract getLibp2pNode(sixId: string): LibP2pWithServices | null;

  /**
   * Get Helia node for a specific six.
   */
  abstract getHeliaNode(sixId: string): HeliaNode | null;

  /**
   * Get all six IDs that have initialized stacks.
   */
  abstract getAllSixIds(): string[];

  /**
   * Check if a specific six has an initialized stack.
   */
  abstract hasStack(sixId: string): boolean;

  /**
   * Get connection information for a specific six.
   * Returns array of active peer connections with details.
   */
  abstract getConnectionInfo(sixId: string): Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }>;

  /**
   * Signal that Bitswap appears stalled for a given six.
   * The service decides how to recover (e.g., force reconnect to bootstrap).
   * Fire-and-forget — callers should not await this.
   */
  abstract reportBitswapStall(sixId: string): void;
}
